import { FC } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Avatar, Center, Flex, Image, VStack } from '@chakra-ui/react';

import notepad from 'assets/brand/notepad@2x.png';
import { formatAmount } from 'common/formatAmount';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import routes from 'features/Payments/routes';
import * as Layout from 'layouts/StepperLayout';

import { useScheduledTransfers } from './hooks/useScheduledTransfers';
import locales from './locales';
import ScheduledTransferSidebar from './ScheduledTransferSidebar';
import { getLocaleFromFrequency } from './utils';

interface ScheduledTransferContentParams {
  selectedScheduledTransferId?: string;
}

const ScheduledTransfersContent: FC<ScheduledTransferContentParams> = ({
  selectedScheduledTransferId,
}) => {
  const {
    error,
    fetchMore,
    hasNextPage,
    isFetchingMore,
    loading,
    scheduledTransfers,
  } = useScheduledTransfers();

  if (error) {
    return <ErrorView />;
  }

  if (loading) {
    return <Loader />;
  }

  if (scheduledTransfers.length === 0) {
    return (
      <VStack
        alignItems="center"
        justifyContent="center"
        paddingTop="space-24"
        spacing="space-32"
      >
        <Image alt="" boxSize="7.5rem" role="presentation" src={notepad} />
        <VStack alignItems="center" spacing="space-8">
          <Text fontSize="font-18" fontWeight="weight-500">
            {locales.noScheduledTransfers}
          </Text>
          <Text>{locales.noScheduledTransfersDescription}</Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <>
      <SunshineCard.Group>
        {scheduledTransfers.map(
          ({
            amount,
            bankTransferRecipient,
            frequency,
            label,
            scheduledBankTransferId,
          }) => (
            <SunshineCard
              alignItems="center"
              as={Link}
              flexDir="row"
              isActive={scheduledBankTransferId === selectedScheduledTransferId}
              key={scheduledBankTransferId}
              to={`${routes.scheduledTransfers}/${scheduledBankTransferId}`}
            >
              <SunshineCard.Slot name="aside">
                <Avatar
                  backgroundColor="grey.200"
                  borderRadius="radius-12"
                  color="grey.800"
                  fontWeight="weight-500"
                  name={label || bankTransferRecipient.name}
                  size="md"
                />
              </SunshineCard.Slot>
              <Flex gap="space-24" justifyContent="space-between">
                <Flex flexDir="column" width={400}>
                  <SunshineCard.Title
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {label ||
                      locales.formatString(locales.toBeneficiary, {
                        beneficiary: bankTransferRecipient.name,
                      })}
                  </SunshineCard.Title>

                  <SunshineCard.Text>
                    {getLocaleFromFrequency(frequency)}
                  </SunshineCard.Text>
                </Flex>
                <Text
                  fontWeight="weight-500"
                  textAlign="right"
                  variant="primary"
                >
                  {formatAmount({
                    currency: 'EUR',
                    forcePlusSign: false,
                    value: -amount,
                  })}
                </Text>
              </Flex>
            </SunshineCard>
          ),
        )}
      </SunshineCard.Group>
      {hasNextPage ? (
        <Center paddingTop="space-32">
          <Button
            icon="arrow-down"
            isLoading={isFetchingMore}
            onClick={fetchMore}
            variant="secondary"
          >
            {locales.fetchMore}
          </Button>
        </Center>
      ) : null}
    </>
  );
};

const ScheduledTransfers: FC = () => {
  const { scheduledTransferId } = useParams<{
    scheduledTransferId?: string;
  }>();

  const { bankAccounts } = useCompanyBankAccounts();

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton to={routes.root} />
        </Layout.HeaderButtonsContainer>
        <Layout.BodyScrollableSection>
          <VStack alignItems="stretch" spacing="space-32">
            <Heading alignSelf="flex-start" size="lg" textAlign="left">
              {locales.title}
            </Heading>
            <ScheduledTransfersContent
              selectedScheduledTransferId={scheduledTransferId}
            />
          </VStack>
        </Layout.BodyScrollableSection>
      </Layout.Body>
      {scheduledTransferId ? (
        <ScheduledTransferSidebar bankAccounts={bankAccounts} />
      ) : null}
    </Layout.Root>
  );
};

export default ScheduledTransfers;
